import {
  ref,
  reactive,
  computed,
  Ref,
  ComputedRef,
} from '@vue/composition-api';
import { getQuickWithdrawConfig } from '@/api/quick-withdraw';
import {
  getApplyProduct,
  calcRepayPlans,
  getLoanUsageList,
  submitOrderApply,
  getLoanContracts,
  checkNeedTradePasswd,
} from '@/api/apply-product';
import { getRuleSetCouponList } from '@/api/coupon';
import { getSuperMemberCardSummary } from '@/api/rights';
import { getAgreementsByCommodities } from '@/api/contract';
import store from '@/store';
import {
  SubmitApplyResultData,
  RouteMarketEnum,
  PeriodRuleItemData,
  LoanModelEnum,
  RemoteContract,
  EquityPathType,
  AccountType,
  LoanUsageOption,
} from '@/api/apply-product/types';
import { RepaymentModel, repayPlanModelType } from '@/po/ProductResult';

import type {
  CouponRolePo,
  CouponTypeEnum,
} from '@/api/coupon/types';
import {
  PackageBuyStatusEnum,
  RightPackageSourceEnum,
  RightPackageTypeEnum,
  SuperMemberSourceEnum,
} from '@/api/rights/types';
// 导入提现页的优惠券默认选中逻辑
import { getDefaultSelectedCoupon } from '@/views_v2/apply-product/utils';
// 导入借款用途排序方法
import { sortLoanUsageListByCapitalCode } from '@/shared/utils/sortLoanUsageList';
// 导入隐私授权协议校验服务
import { agreementService } from '@/service/confirm-agreements';
import { Commodity, Contract } from '@/api/contract/types';
import { ContractEnum } from '@/enums/contract-type';
import { ReplanResultType } from '@/views_v2/apply-product/types';
import { livenessPop } from '@/components/liveness-pop';
import { LivenessProjectEnum, LivenessSceneEnum } from '@/enums/liveness-param';
import router from '@/router';
import { RouterV2Enum } from '@/router/router-names';
import TradeKey from '@/components/trade-key-pop';
import { CALC_REPAYPLANS_VERSION } from '@/constant';
import { CreditDataType } from '@/api/homeV2Status/types';
import { FormInfomation } from '@/views_v2/card-list/types';
import { nativeLocalGetData, nativeLocalRemoveData, setNativeNavBar } from '@/api/native/utils';
import { queryBuyStatus } from '@/api/rights/order/order';
import { ubtSendError } from '@/shared/utils/ubt';
import { Toast, Dialog } from '@hb/coffee';
import { ResponseCodeEnum } from '@/enums/response-code';
import { rightsManager } from '@/widgets/loan-right/RightsManager';
import { TabTypeEnum } from '@/widgets/loan-right/types';
import { PricingTypeEnum } from '@/enums/rights';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import type {
  BillCalcParams,
  PageDataState,
} from '../types';
import { TP_QUICK_WITHDRAW_CARD_VERIFICATION_POP } from '../tianqi';


/**
 * 检查优惠券是否有效
 * @param coupon 优惠券对象
 * @returns 是否有效
 */
function isValidCoupon(coupon: CouponRolePo | null): boolean {
  return !!(coupon?.amount && coupon.availability);
}

/**
 * 快速提现页面数据管理Hook
 */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

export function useQuickWithdrawData(userCreditData: CreditDataType | null): {
  pageData: PageDataState;
  selectedParams: BillCalcParams;
  selectedCoupon: Ref<CouponRolePo | null>;
  formattedAmount: ComputedRef<string>;
  formattedRepaymentInfo: ComputedRef<string>;
  shouldShowSuperMember: ComputedRef<boolean>;
  fetchConfig: () => Promise<void>;
  fetchProductInfo: (productId: string, capitalCode?: string) => Promise<void>;
  fetchBillCalc: () => Promise<void>;
  fetchCouponList: (channelId?: string, funderCode?: string) => Promise<void>;
  fetchSuperCardInfo: () => Promise<void>;
  fetchLoanPurposeDict: () => Promise<void>;
  fetchContracts: () => Promise<void>;
  setBankCardInfo: (bankCardInfo: {
    bankName: string;
    bankNo: string;
    bankIconUrl: string;
    userBankCardId: string;
  } | null) => void;
  fetchLocalBankCardInfo: () => Promise<void>;
  selectCoupon: (coupon: CouponRolePo) => void;
  cancelCoupon: () => void;
  selectLoanPurpose: (purpose: LoanUsageOption) => void;
  initializePageData: () => Promise<void>;
  refreshPageData: () => Promise<void>;
  submitOrder: () => Promise<SubmitApplyResultData | null>;
  submitOrderWithLiveness: () => Promise<void>;
  validateSubmitData: () => { isValid: boolean; errors: string[] };
  // 协议校验相关方法
  checkPrivacyAgreement: () => Promise<boolean>;
  // 判断是否需要交易密码
  checkNeedTradePasswd: () => Promise<void>;
  // 获取交易密码token
  getTradePasswdToken: () => Promise<void>;
  // 短信验证码相关方法
  handleSubmitSmsCode: (smsCode: string) => Promise<void>;
  hideSmsDialog: () => void;
  // 权益tab切换
  handleRightTabChange: (tab: TabTypeEnum, cb?: () => void) => void;
  hideTradeKeyDialog: () => void;
  isTradeKeyDialogOpen: () => boolean;
  } {
  // 页面数据状态
  const pageData: PageDataState = reactive<PageDataState>({
    config: null,
    productInfo: null,
    billCalc: null,
    couponList: [],
    superCardInfo: null,
    superCardSelected: false,
    superMemberBuyStatus: null, // 初始化为null
    loanPurposeDict: [],
    selectedLoanPurpose: null,
    // 提现协议相关字段 - 暂时空着等后续指令
    contracts: [],
    // 银行卡信息
    bankCardInfo: null,
    submitResult: null,
    capitalCode: '',
    routeMarket: RouteMarketEnum.ORIGINAL,
    loanUsage: '',
    checkId: '',
    needTradePasswd: false,
    tradePasswdToken: '',
    smsCode: '',
    showSmsCode: false,
  });

  // 当前选中的参数
  const selectedParams: BillCalcParams = reactive<BillCalcParams>({
    amount: 0,
    period: {
      period: 0,
      periodUnit: 'MONTH',
      periodRate: 0,
      rateUnit: 'DAY',
    } as PeriodRuleItemData,
    repaymentMethod: '',
  });

  // 防抖定时器
  let billCalcTimeout: NodeJS.Timeout | null = null;

  // 计算属性：格式化后的金额显示
  const formattedAmount = computed((): string => {
    if (!pageData.config?.quickLoanChannel?.availableAmount) return '0';
    return String(pageData.config?.quickLoanChannel?.availableAmount).toLocaleString();
  });

  // 计算属性：格式化后的还款信息
  const formattedRepaymentInfo = computed((): string => {
    if (!pageData.billCalc) return '当前渠道资金不足，请去提现其他额度';

    const { loanCalcList, shouldPayAmount } = pageData.billCalc;
    if (!loanCalcList || loanCalcList.length === 0) return '当前渠道资金不足，请去提现其他额度';
    if (pageData.billCalc.calcResult === ReplanResultType.REPLAN_FAILED) return '当前渠道资金不足，请去提现其他额度';

    const firstRepayDate = new Date(loanCalcList[0]?.dueDate || '');
    const month = firstRepayDate.getMonth() + 1;
    const day = firstRepayDate.getDate();
    const firstRepay = `${month}月${day}日`;
    return `${String(loanCalcList.length)}个月 首期${firstRepay} 应还${Number(shouldPayAmount || 0).toFixed(2)}元`;
  });

  // 赋值方法A：设置银行卡信息
  const setBankCardInfo = (bankCardInfo: {
    bankName: string;
    bankNo: string;
    bankIconUrl: string;
    userBankCardId: string;
  } | null) => {
    pageData.bankCardInfo = bankCardInfo;
  };

  // 获取本地银行卡信息
  const fetchLocalBankCardInfo = async (): Promise<void> => {
    try {
      const quickWithdrawCardData = await nativeLocalGetData({
        key: FormInfomation.QuickWithdraw,
      }) as string[];

      if (quickWithdrawCardData?.length) {
        const [bankName, bankNo, bankIconUrl, userBankCardId] = quickWithdrawCardData;
        setBankCardInfo({
          bankName,
          bankNo,
          bankIconUrl,
          userBankCardId,
        });

        // 赋值成功以后，移除本地数据
        await nativeLocalRemoveData({
          key: FormInfomation.QuickWithdraw,
        });
      }
    } catch (error) {
      ubtSendError(error instanceof Error ? error : new Error(`localbankcardinfo error: ${String(error)}`));
    }
  };

  // 计算属性：是否显示超级会员卡片
  const shouldShowSuperMember = computed(() => {
    const showByConfig = pageData.config?.quickLoanChannel
      ?.capitalList?.[0]?.showSuperMember === true;
    const showByBuyStatus = pageData.superMemberBuyStatus === PackageBuyStatusEnum.BOUGHT;
    /** 如果购买状态为已购买，则不显示超级会员卡片 */
    if (showByBuyStatus) {
      return !showByBuyStatus;
    }
    return showByConfig;
  });

  // 计算属性：选中的优惠券信息
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const selectedCoupon = ref<CouponRolePo | null>(null);


  /**
   * 获取快速提现配置
   */
  const fetchConfig = async () => {
    try {
      if (!userCreditData) {
        return;
      }

      const { creditStatus } = userCreditData;
      if (!creditStatus) {
        return;
      }

      const result = await getQuickWithdrawConfig({
        deviceJson: store.state.base.deviceInfo
          ? store.state.base.deviceInfo
          : undefined,
        cityInfoJson: store.state.base.cityInfo
          ? store.state.base.cityInfo
          : undefined,
      });
      if (result) {
        pageData.config = result;
        // 更新选中参数
        if (result.quickLoanChannel?.availableAmount) {
          selectedParams.amount = result.quickLoanChannel.availableAmount;
        }
        if (result.quickLoanChannel?.capitalList?.[0]) {
          const capitalItem = result.quickLoanChannel.capitalList[0];
          const { routeMarket, capitalListDetail = [] } = capitalItem;

          // 参考提现页逻辑：串行模式时多个资方代码用下划线拼接
          pageData.capitalCode = routeMarket === RouteMarketEnum.SERIAL
            ? capitalListDetail.map((detail) => detail.capitalCode).join('_')
            : capitalListDetail[0]?.capitalCode || capitalItem.capitalCode || '';
        }
        if (result.quickLoanChannel?.capitalList?.[0]?.routeMarket) {
          pageData.routeMarket = result.quickLoanChannel.capitalList[0]
            .routeMarket;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('获取快速提现配置失败:', error);
    }
  };

  /**
   * 获取产品信息
   */
  const fetchProductInfo = async (productId: string, capitalCode?: string) => {
    try {
      const result = await getApplyProduct({
        productId,
        capitalCode,
        fundRouteResponseId: pageData.config?.quickLoanChannel?.fundRouteResponseId,
      });
      if (result) {
        pageData.productInfo = result;

        // 设置银行卡信息（如果接口返回了银行卡信息且当前没有本地银行卡信息）
        if (result.userCredit && !pageData.bankCardInfo) {
          setBankCardInfo({
            bankName: result.userCredit.bankName,
            bankNo: result.userCredit.bankNo,
            bankIconUrl: result.userCredit.bankIconUrl,
            userBankCardId: result.userCredit.userBankCardId,
          });
        }

        // 设置默认还款方式
        const defaultMethod = result.productInfo?.repaymentModel?.[0];
        if (defaultMethod) {
          selectedParams.repaymentMethod = defaultMethod;
        }
        const defaultPeriod = result.productInfo?.periodRules[0].periodRule[0];
        if (defaultPeriod) {
          selectedParams.period = defaultPeriod;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('获取产品信息失败:', error);
    }
  };

  /**
   * 获取试算结果
   */
  const fetchBillCalc = async () => {
    try {
      const result = await calcRepayPlans({
        amount: Number(pageData.config?.quickLoanChannel?.availableAmount || 0),
        period: Number(selectedParams.period?.period),
        productId: pageData.productInfo?.productInfo?.productId || '',
        repayType:
          repayPlanModelType[selectedParams.repaymentMethod as string]
          || repayPlanModelType.EPAI,
        couponId: String(selectedCoupon.value?.couponId || ''),
        bankCardId: String(pageData.bankCardInfo?.userBankCardId || ''),
        capitalCode: pageData.capitalCode || '',
        fundRouteResponseId: pageData.config?.quickLoanChannel?.fundRouteResponseId || '',
        bizVersion: CALC_REPAYPLANS_VERSION,
        // 权益选择,首次不传入由资金侧来路由是否需要权益
        equitySelected: pageData.billCalc ? rightsManager.getEquitySelected() : undefined,
      });
      if (result) {
        pageData.billCalc = result;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('试算失败:', error);
    }
  };

  /**
   * 防抖的试算函数
   * @param params 试算参数
   */
  const debouncedFetchBillCalc = (cb?: () => void) => {
    if (billCalcTimeout) {
      clearTimeout(billCalcTimeout);
    }
    billCalcTimeout = setTimeout(async () => {
      await fetchBillCalc();
      if (cb) {
        cb();
      }
    }, 300);
  };

  /**
   * 获取优惠券列表
   */
  const fetchCouponList = async (channelId?: string, funderCode?: string) => {
    try {
      const result = await getRuleSetCouponList({
        productId: channelId || '',
        capitalCode: funderCode || '',
        pageIndex: 1,
        pageSize: 1000,
        selCurrentPeriod: Number(selectedParams.period?.period),
        repayType: repayPlanModelType[selectedParams.repaymentMethod as string]
          || repayPlanModelType.EPAI,
        loanAmount: Number(selectedParams.amount),
        maxAmount: Number(pageData.config?.quickLoanChannel?.availableAmount || 0),
      });
      if (result) {
        // 保留所有优惠券，包括不可用的，让组件自己处理分类
        pageData.couponList = result;

        // 使用提现页的默认选中逻辑，自动选择优先级最高的优惠券（只从可用优惠券中选择）
        const availableCoupons = pageData.couponList.filter((item) => !!item.availability);
        const defaultCoupon = getDefaultSelectedCoupon(availableCoupons);
        if (defaultCoupon) {
          selectedCoupon.value = defaultCoupon;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('获取优惠券列表失败:', error);
    }
  };

  /**
   * 获取超级会员信息
   */
  const fetchSuperCardInfo = async () => {
    try {
      const result = await getSuperMemberCardSummary({
        productId: pageData.config?.quickLoanChannel?.productId || '',
        rateValue: pageData.billCalc?.rateValue || 0,
        pricingType: pageData.billCalc?.pricingType,
        from: SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY,
      });
      if (result) {
        pageData.superCardInfo = result;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('获取超级会员信息失败:', error);
    }
  };

  /**
   * 获取借款用途字典
   */
  const fetchLoanPurposeDict = async () => {
    try {
      const result = await getLoanUsageList();
      if (result) {
        // 使用公共方法对借款用途列表进行排序
        const sortedResult = sortLoanUsageListByCapitalCode(pageData.capitalCode, result);

        pageData.loanPurposeDict = sortedResult;
        // 设置默认选中的借款用途（第一个可用的选项）
        const defaultPurpose = sortedResult[0];
        if (defaultPurpose) {
          pageData.selectedLoanPurpose = defaultPurpose;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('获取借款用途失败:', error);
    }
  };

  /**
   * 多资方串行模式协议获取 - 参考 multiCapitalContracts 方法实现
   */
  const fetchMultiCapitalContracts = async () => {
    const { productId = '', fundRouteResponseId = '' } = pageData.config?.quickLoanChannel || {};
    const params = {
      commodityIds: [Number(productId)],
      amount: selectedParams.amount ? Number(selectedParams.amount) * 100 : undefined, // 转换为分
      period: selectedParams.period ? Number(selectedParams.period.period) : undefined,
      loanUsage: pageData.selectedLoanPurpose?.code || '',
      bankCardId: pageData.bankCardInfo?.userBankCardId || '',
      orderId: pageData.submitResult?.loanOrderId || '',
      type: ContractEnum.APPLY, // 提现申请协议
      capitalCode: pageData.capitalCode || '',
      fundRouteResponseId,
      /** 扩展字段，透传给后端 */
      extendObj: pageData.config?.extendObj || {},
    };

    // 调用获取协议接口
    const commodities: Commodity[] = await getAgreementsByCommodities(params);

    // 处理没有资方名称的数据：将其 contractList 合并到有资方名称的项中
    const itemsWithoutProductName = commodities.filter((item) => !item.productName);
    const itemsWithProductName = commodities.filter((item) => item.productName);

    // 将没有 productName 的项的 contractList 添加到有 productName 的项的 contractList 后面
    if (itemsWithoutProductName.length > 0 && itemsWithProductName.length > 0) {
      const contractsToMerge = itemsWithoutProductName.reduce<Contract[]>(
        (acc, item) => [...acc, ...item.contractList],
        [],
      );
      itemsWithProductName.forEach((item) => {
        item.contractList = [...item.contractList, ...contractsToMerge];
        // 如果原先没有 creditRouteChannel 字段，则从 itemsWithoutProductName 中查找并赋值
        if (!item.creditRouteChannel) {
          const creditRouteChannelItem = itemsWithoutProductName.find(
            (itemWithoutName) => itemWithoutName.creditRouteChannel,
          );
          if (creditRouteChannelItem?.creditRouteChannel) {
            item.creditRouteChannel = creditRouteChannelItem.creditRouteChannel;
          }
        }
      });
    }

    // 使用处理后的数据（只保留有 productName 的项，如果都没有则保留原数据）
    const processedCommodities = itemsWithProductName.length > 0
      ? itemsWithProductName
      : commodities;

    // 更新页面数据
    pageData.contracts = processedCommodities;
  };

  /**
   * 单资方模式协议获取 - 参考 getContracts 方法实现
   */
  const fetchSingleCapitalContracts = async () => {
    const params = {
      commodityId: String(pageData.config?.quickLoanChannel?.productId || ''),
      amount: selectedParams.amount ? String(Number(selectedParams.amount) * 100) : '', // 转换为分
      period: selectedParams.period ? String(selectedParams.period.period) : '',
      loanUsage: pageData.selectedLoanPurpose?.code || '',
      bankCardId: pageData.bankCardInfo?.userBankCardId || '',
      orderId: pageData.submitResult?.loanOrderId || '',
      repaymentModelName: RepaymentModel.EPAI,
      type: ContractEnum.APPLY, // 提现申请协议
      showAllinPayContract: false, // 快速提现暂不需要通联绑卡协议
      capitalCode: pageData.capitalCode || '',
      /** 扩展字段，透传给后端 */
      extendObj: pageData.config?.extendObj || {},
    };

    // 调用获取协议接口
    const contracts: RemoteContract[] = await getLoanContracts(params);

    const creditRouteChannelItem = contracts?.length > 0
      ? contracts.find((item) => item.creditRouteChannel) : undefined;

    // 将 Commodity[] 转换为 RemoteContract[] 格式
    const newContracts: Commodity[] = [];
    newContracts.push({
      productId: '',
      productName: '',
      productIcon: '',
      contractList: contracts,
      /** 征信路由渠道数据拼接 */
      creditRouteChannel: creditRouteChannelItem?.creditRouteChannel || '',
    });

    // 处理协议数据
    // RemoteContract 不需要设置 contractActive 和 isExpand 属性
    // 更新页面数据
    pageData.contracts = newContracts;
  };

  /**
   * 获取协议数据 - 参考提现页逻辑，区分串行和非串行模式
   */
  const fetchContracts = async () => {
    // 检查必要的数据是否存在
    if (!pageData.config?.quickLoanChannel?.productId) {
      // eslint-disable-next-line no-console
      console.warn('产品ID不存在，无法获取协议数据');
      return;
    }

    // 参考提现页逻辑：根据routeMarket判断是否串行模式
    if (pageData.routeMarket === RouteMarketEnum.SERIAL) {
      // 串行模式：调用多资方协议获取逻辑
      await fetchMultiCapitalContracts();
    } else {
      // 非串行模式：调用普通协议获取逻辑
      await fetchSingleCapitalContracts();
    }
  };

  /**
   * 选择优惠券
   * @param coupon 优惠券对象
   */
  const selectCoupon = (coupon: CouponRolePo) => {
    // 验证优惠券有效性
    if (!isValidCoupon(coupon)) {
      // eslint-disable-next-line no-console
      console.warn('尝试选择无效的优惠券:', coupon);
      return;
    }

    selectedCoupon.value = coupon;

    // 重新试算（使用防抖）
    if (pageData.config) {
      debouncedFetchBillCalc();
    }
  };

  /**
   * 取消选择优惠券
   */
  const cancelCoupon = () => {
    selectedCoupon.value = null;

    // 重新试算（使用防抖）
    if (pageData.config) {
      debouncedFetchBillCalc();
    }
  };

  /**
   * 选择借款用途
   */
  const selectLoanPurpose = (purpose: LoanUsageOption) => {
    pageData.selectedLoanPurpose = purpose;
  };

  /**
   * 权益tab切换
   */
  const handleRightTabChange = async (tab: TabTypeEnum, cb?: () => void) => {
    if (pageData.billCalc?.pricingType === PricingTypeEnum.LOAN_24_MUST_RIGHTS_OR_LOAN_36) {
      if (pageData.config) {
        debouncedFetchBillCalc(rightsManager.getSummaryData);
      }
    } else {
      await rightsManager.getSummaryData();
    }
    if (cb) {
      cb();
    }
  };

  /**
   * 验证提交数据
   */
  const validateSubmitData = () => {
    const errors: string[] = [];
    if (pageData.billCalc) {
      const calcResultStatus = (
        pageData.billCalc.calcResult === ReplanResultType.REPLAN_FAILED
      ) || !pageData.billCalc;
      if (calcResultStatus) {
        errors.push('当前渠道资金不足，请去提现其他额度');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  /**
   * 获取权益相关请求数据-超级会员卡片数据先做区分
   */
  const getMarketReq = async () => {
    if (shouldShowSuperMember.value) {
      const { superCardInfo, superCardSelected } = pageData;
      // 只有在用户选择了超级会员且超级会员信息存在时才返回数据
      if (superCardSelected && superCardInfo && Object.keys(superCardInfo).length > 0) {
        return {
          packageType: superCardInfo.packageType,
          packageId: superCardInfo.packageId,
          packageFrom: SuperMemberSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY, // 快速提现页面来源
        };
      }
      return {
        equityPath: EquityPathType.NOT_NEED_RIGHT,
      };
    }
    const rightData = await rightsManager.getData();
    return rightData?.loanApplyMarketReq;
  };

  /**
   * 获取交易密码token
   * 如果需要交易密码，则唤起交易密码弹窗并赋值
   */
  const getTradePasswdToken = async (): Promise<void> => {
    if (!pageData.needTradePasswd) {
      return;
    }

    const productId = pageData.config?.quickLoanChannel?.productId;
    if (!productId) {
      throw new Error('产品ID不存在，无法获取交易密码');
    }

    // 交易密码弹窗打开时, 因为首页没有导航栏，需要显示导航栏
    setNativeNavBar(true);
    try {
      ubtSetupModuleExpose(TP_QUICK_WITHDRAW_CARD_VERIFICATION_POP, {
        model: '1',
      });
      const token = await TradeKey.getToken({ commodityId: productId });
      pageData.tradePasswdToken = token;
      // 关闭导航栏
      setNativeNavBar(false);
    } catch (error) {
      // 关闭导航栏
      setNativeNavBar(false);
      // 用户取消或交易密码获取失败，抛出错误
      throw new Error('交易密码获取失败，请重试');
    }
  };

  /**
   * 处理交易密码错误弹窗
   */
  const handleTradePasswordError = async (
    errorMessage: string,
    doSubmit: () => Promise<SubmitApplyResultData>,
  ): Promise<SubmitApplyResultData> => new Promise((resolve, reject) => {
    Dialog.alert({
      title: '交易密码失败',
      message: errorMessage,
      closeable: false,
      confirmButtonText: '输入交易密码',
    }).then(async () => {
      try {
        // 重新获取交易密码token
        await getTradePasswdToken();
        // 重新提交订单（调用内部的doSubmit方法，避免递归）
        const result = await doSubmit();
        resolve(result);
      } catch (err) {
        reject(err);
      }
    }).catch(() => {
      // 用户取消，拒绝promise
      reject(new Error('用户取消输入交易密码'));
    });
  });

  /**
   * 实际执行提交订单的逻辑（内部方法）
   */
  const doSubmitOrder = async (): Promise<SubmitApplyResultData> => {
    // 构建提交参数
    const { quickLoanChannel: config } = pageData.config || {};
    const { userCredit: productInfo } = pageData.productInfo || {};
    const { billCalc } = pageData;
    const { selectedLoanPurpose: loanPurpose } = pageData;

    /** 查找是否返回的征信协议 */
    const hasCreditRouteChannel = pageData.contracts.filter((item) => item.creditRouteChannel);

    if (!config || !productInfo || !billCalc || !loanPurpose) {
      throw new Error('提交数据不完整');
    }
    const submitParams = {
      amount: config.availableAmount || 0,
      productId: config.productId || '',
      creditApplyId: config.creditApplyId || '',
      /** 资金路由Id(埋点使用) */
      fundRouteResponseId: config.fundRouteResponseId || '',

      applyPeriod: Number(selectedParams.period?.period),
      applyPeriodUnit: selectedParams.period?.periodUnit,
      repaymentModel: selectedParams.repaymentMethod as string || RepaymentModel.EPAI,

      couponId: (selectedCoupon.value?.couponId || '') as string,
      couponType: (selectedCoupon.value?.couponType || '') as string,
      freeCouponType: (selectedCoupon.value?.freeCouponType || '') as CouponTypeEnum | '',

      /** 借款用途 */
      loanUsage: loanPurpose.code,

      /** 银行卡id */
      userBankCardId: pageData.bankCardInfo?.userBankCardId || '',

      /** 显示利率 */
      interestDisplay: billCalc.rateValue || 0, //
      /** 利率周期单位 */
      interestUnitDisplay: billCalc.rateType || 'DAY',
      /** 试算流水号 */
      trialFlowNo: billCalc.trialFlowNo || '',
      /** AMC标识 */
      amcFlag: billCalc.amcFlag || false,
      /** 资金code */
      capitalCode: pageData.capitalCode || '',
      /** 渠道市场 */
      routeMarket: pageData.routeMarket,
      /** 下单前checkId */
      checkId: pageData.checkId || '',

      /** 权益相关数据 */
      loanApplyMarketReq: await getMarketReq(),
      /** 是否预授信召回挽留流程 */
      recallPreCredit: false,
      /** 快速提现标识 */
      loanModel: LoanModelEnum.QUICK,
      /** 征信路由渠道 */
      creditRouteChannel: (hasCreditRouteChannel[0]
        && hasCreditRouteChannel[0].creditRouteChannel
      ),
      /** 是否提现-授信 一体化，默认 false  */
      loanWithCredit: false,
      /** 提现时 授信一体化 渠道 信息，快速提现场景下，不传,如果loanWithCredit = true，则传['8180'] */
      loanWithCreditProductList: [],
      /** 短信验证码 */
      smsCode: pageData.smsCode || '',
      /** 交易密码 需要的场景才传 */
      passwordToken: pageData.tradePasswdToken || '',
      /** 扩展字段，限额相关透传字段，前端透传给后端用来标识当笔提现的相关状态 */
      extendObj: pageData?.config?.extendObj ? {
        ...pageData?.config?.extendObj,
      } : {},
      /** 检查类型，度小满场景 wait false 需要，其他场景不需要
       */
      checkType: '',
      /** 当前账号类型，固定银行卡，后面考虑可以删除该字段 */
      accountType: AccountType.BANK,
    };

    const result = await submitOrderApply(submitParams);
    if (result && result.applyOrderCheckVo) {
      pageData.checkId = result.applyOrderCheckVo.checkId || '';
    }
    return result;
  };

  /**
   * 提交订单
   */
  const submitOrder = async (): Promise<SubmitApplyResultData> => {
    try {
      // 验证数据
      const validation = validateSubmitData();
      if (!validation.isValid) {
        throw new Error('提交数据验证失败');
      }

      // 判断是否需要交易密码，如果需要则唤起交易密码弹窗并赋值
      await getTradePasswdToken();

      // 执行提交
      return await doSubmitOrder();
    } catch (error) {
      // 检查是否是交易密码错误
      interface ErrorWithCode extends Error {
        code?: number;
        msg?: string;
      }
      const errorWithCode = error as ErrorWithCode;
      const errCode = Number(errorWithCode?.code || 0);
      if (
        [
          ResponseCodeEnum.PASSWORD_TOKEN_IS_ERROR,
          ResponseCodeEnum.PASSWORD_TOKEN_IS_NULL,
        ].indexOf(errCode) >= 0
      ) {
        // 清除接口异常Toast，否则Toast会盖在Dialog上面
        Toast.clear();
        const tradeErrMessage = errorWithCode?.message || errorWithCode?.msg || '您的交易密码已失效，请重新输入';
        // 处理交易密码错误，显示弹窗并重新提交
        return handleTradePasswordError(tradeErrMessage, doSubmitOrder);
      }

      const errorMessage = error instanceof Error ? error.message : '提交失败，请稍后重试';
      throw new Error(errorMessage);
    }
  };

  /**
   * 提交订单 - 包含活体认证处理和跳转逻辑
   */
  const submitOrderWithLiveness = async (): Promise<void> => {
    try {
      // 1. 提交订单
      const submitResult = await submitOrder();

      const {
        applyOrderCheckVo: applyCheck, loanOrderId,
      } = submitResult;

      // 中断订单情况, submitResult 的 loanOrderId 会被赋值，需要判断 applyCheck 是否有值，再往下走流程
      if (!applyCheck) {
        return;
      }

      if (!applyCheck.faceCheck) {
        // 调用活体认证, 因为首页没有导航栏，需要显示导航栏
        setNativeNavBar(true);
        livenessPop.getOrderId({
          project: LivenessProjectEnum.ZYQ,
          scene: LivenessSceneEnum.Apply,
          commodityId: pageData.config?.quickLoanChannel?.productId || '',
        }).then(() => {
          setNativeNavBar(false);
          submitOrderWithLiveness();
        }).catch((error) => {
          /* 活体失败，关闭原生导航栏 */
          setNativeNavBar(false);
          /** 不做活体的时候，或者活体失败的时候，需要清空 checkId */
          pageData.checkId = '';
          throw new Error('活体认证失败，请重试');
        });
        return;
      }

      /** 需要做短信验证码校验, 展示短信验证码弹窗 */
      if (!applyCheck.sms) {
        ubtSetupModuleExpose(TP_QUICK_WITHDRAW_CARD_VERIFICATION_POP, {
          model: '0',
        });
        pageData.showSmsCode = true;
        return;
      }

      // 跳转到结果页面
      router.push({
        path: RouterV2Enum.applyResult,
        query: {
          orderId: loanOrderId || '',
          commodityId: pageData.config?.quickLoanChannel?.productId || '',
          commodityName: pageData.config?.quickLoanChannel?.productName || '',
        },
      });
      return;
    } catch (error) {
      ubtSendError(error instanceof Error ? error : new Error(`submitorderwithliveness error: ${String(error)}`));
    }
  };

  /**
   * 判断是否需要交易密码
   */
  const checkNeedTradePasswdMethod = async (): Promise<void> => {
    try {
      const productId = pageData.config?.quickLoanChannel?.productId;
      if (productId) {
        const needTradePasswd = await checkNeedTradePasswd(productId);
        pageData.needTradePasswd = needTradePasswd;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('判断是否需要交易密码失败:', error);
      throw error;
    }
  };

  /**
   * 初始化页面数据
   */
  const initializePageData = async () => {
    await fetchConfig();
    if (pageData.config?.quickLoan) {
      await fetchProductInfo(pageData.config?.quickLoanChannel?.productId || '',
        pageData.capitalCode || '');
      // 后面这些可以all
      await fetchCouponList(pageData.config?.quickLoanChannel?.productId || '',
        pageData.capitalCode || '');
      await fetchLoanPurposeDict();
      await fetchBillCalc();
      await rightsManager.updateContext({
        channel: pageData.config?.quickLoanChannel,
        planListData: pageData.billCalc || undefined,
        capitalCode: pageData.capitalCode || '',
        /** 来源标识 */
        from: RightPackageSourceEnum.FINANCE_HOME_CREDIT_LOAN_APPLY,
        amount: (pageData.config?.quickLoanChannel?.availableAmount || 0).toString(),
        period: selectedParams.period?.period,
      }, true);
      await checkNeedTradePasswdMethod();
      /** 需要购买超会的时候 再调用超会概要接口 */
      if (shouldShowSuperMember.value) {
        await fetchSuperCardInfo();
      }
    }
  };

  /**
   * 刷新页面数据 - 用于keep-alive组件激活时
   */
  const refreshPageData = async () => {
    // 只刷新关键数据，避免全量刷新
    try {
      // 重新获取配置数据（可能包含最新的额度信息）
      // 首先获取本地银行卡信息
      await fetchLocalBankCardInfo();
      if (pageData.config?.quickLoan) {
        // 重新获取产品信息
        await fetchProductInfo(pageData.config?.quickLoanChannel?.productId || '',
          pageData.capitalCode || '');


        // 重新获取优惠券列表（可能有新的优惠券）
        await fetchCouponList(pageData.config?.quickLoanChannel?.productId || '',
          pageData.capitalCode || '');

        // 重新试算（获取最新的还款计划）
        await fetchBillCalc();

        // 查询超会购买状态（packageType 与当前展示卡片一致）
        if (shouldShowSuperMember.value) {
          try {
            const pkgType = pageData.superCardInfo?.packageType
              || RightPackageTypeEnum.SUPER_MEMBER_CARD;
            const buyStatusResult = await queryBuyStatus({
              packageType: pkgType,
            });
            pageData.superMemberBuyStatus = buyStatusResult.packageBuyStatus;
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('查询超会购买状态失败:', error);
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('刷新页面数据失败:', error);
      throw error;
    }
  };

  /**
   * 隐私授权协议校验
   */
  const checkPrivacyAgreement = async (): Promise<boolean> => {
    try {
      // 使用项目统一的协议校验服务
      const result = await agreementService.confirmFinanceAgreements();
      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('隐私授权协议校验失败:', error);
      return false;
    }
  };

  /**
   * 处理短信验证码提交
   */
  const handleSubmitSmsCode = async (smsCode: string) => {
    // eslint-disable-next-line no-console
    console.error('handleSubmitSmsCode');
    pageData.smsCode = smsCode;
    await submitOrderWithLiveness();
  };

  /**
   * 隐藏短信验证码弹窗
   */
  const hideSmsDialog = () => {
    // eslint-disable-next-line no-console
    console.error('hideSmsDialog');
    pageData.showSmsCode = false;
    pageData.smsCode = '';
    /** 清空 checkId，重新下单才能获取新的 checkId ，因为是第一次预检下发的短信，需要重新下单才能获取新的 checkId */
    pageData.checkId = '';
  };

  /** 判断交易密码弹窗是否打开 */

  const isTradeKeyDialogOpen = (): boolean => TradeKey.isOpen();
  /**
   * 隐藏交易密码弹窗
   */
  const hideTradeKeyDialog = () => {
    if (isTradeKeyDialogOpen()) {
      TradeKey.close();
    }
  };


  return {
    // 数据状态
    pageData,
    selectedParams,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    selectedCoupon,
    // 计算属性
    formattedAmount,
    formattedRepaymentInfo,
    shouldShowSuperMember,
    // 方法
    fetchConfig,
    fetchProductInfo,
    fetchBillCalc,
    fetchCouponList,
    fetchSuperCardInfo,
    fetchLoanPurposeDict,
    fetchContracts,
    setBankCardInfo,
    fetchLocalBankCardInfo,
    selectCoupon,
    cancelCoupon,
    selectLoanPurpose,
    initializePageData,
    refreshPageData,
    submitOrder,
    submitOrderWithLiveness,
    validateSubmitData,
    // 协议校验相关方法
    checkPrivacyAgreement,
    // 判断是否需要交易密码
    checkNeedTradePasswd: checkNeedTradePasswdMethod,
    // 获取交易密码token
    getTradePasswdToken,
    // 短信验证码相关方法
    handleSubmitSmsCode,
    hideSmsDialog,
    // 权益tab切换
    handleRightTabChange,
    hideTradeKeyDialog,
    isTradeKeyDialogOpen,
  };
}
